import { Box, BoxProps, Text } from "@chakra-ui/react";
import { useTheme } from "@emotion/react";
import React from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { PerformanceData } from "src/rest/hedgeFarm";
import { ThemeColor } from "src/theme/palette";

interface Props extends BoxProps {
    data: PerformanceData["historicalPerformance"];
}

export const ChartAlpha1 = ({ data, ...props }: Props) => {
    const { palette } = useTheme();

    const xAxisTickFormatter = (tick: Date) => {
        const month = tick.toLocaleString("en-US", { month: "short" });
        const year = tick.getFullYear().toString().slice(-2);
        return `${month} '${year}`;
    };

    const yAxisTickFormatter = (tick: number) => {
        return `${(tick / 1000).toFixed(0)}K`;
    };

    const legendFormatter = (value: string) => {
        const name = value === "live" ? "Live Data" : "Backtested Data";
        return (
            <Text as={"span"} color={"gray.500"}>
                {name}
            </Text>
        );
    };

    return (
        <Box fill={"gray.400"} stroke={ThemeColor.LINES} {...props}>
            <ResponsiveContainer width={"100%"} height={220}>
                <AreaChart data={data.performance}>
                    <defs>
                        <linearGradient id={"gradientBack"} x1={"0"} y1={"0"} x2={"0"} y2={"1"}>
                            <stop offset={"5%"} stopColor={palette.actionPrimary} stopOpacity={0.2} />
                            <stop offset={"95%"} stopColor={palette.actionPrimary} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id={"gradientLive"} x1={"0"} y1={"0"} x2={"0"} y2={"1"}>
                            <stop offset={"5%"} stopColor={palette.actionWarning} stopOpacity={0.2} />
                            <stop offset={"95%"} stopColor={palette.actionWarning} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray={"1 2"} stroke={"inherit"} />
                    <XAxis
                        dataKey={"date"}
                        tick={{ fill: "inherit", fontSize: "1.2rem" }}
                        tickFormatter={xAxisTickFormatter}
                        stroke={"inherit"}
                        interval={10}
                        dx={19}
                    />
                    <YAxis
                        tick={{ fill: "inherit", fontSize: "1.2rem" }}
                        stroke={"inherit"}
                        width={23}
                        domain={["dataMin", "dataMax"]}
                        tickFormatter={yAxisTickFormatter}
                    />
                    <Area
                        dataKey={"backtest"}
                        stroke={palette.actionPrimary}
                        fillOpacity={1}
                        fill={"url(#gradientBack)"}
                    />
                    <Area dataKey={"live"} stroke={palette.actionWarning} fillOpacity={1} fill={"url(#gradientLive)"} />
                    <Legend iconType={"plainline"} formatter={legendFormatter} />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};
